"use client";

import { useEffect, useRef } from "react";

const UTM_KEY = "wfd_utm_params";
const UTM_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30天

const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
  "source",
  "ref",
] as const;

interface UtmData {
  [key: string]: string;
  first_visit_at: string;
  landing_page: string;
  referrer: string;
}

export function useUtmTracker() {
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current || typeof window === "undefined") return;
    hasRun.current = true;

    const params = new URLSearchParams(window.location.search);
    const hasUtm = UTM_PARAMS.some((p) => params.has(p));

    // 如果 URL 中有 UTM 参数，更新存储
    if (hasUtm) {
      const data: Partial<UtmData> = {};
      for (const key of UTM_PARAMS) {
        const val = params.get(key);
        if (val) data[key] = val;
      }
      data.landing_page = window.location.pathname + window.location.search;
      data.referrer = document.referrer || "direct";
      data.first_visit_at = new Date().toISOString();

      try {
        const stored = localStorage.getItem(UTM_KEY);
        let existing: Partial<UtmData> = {};
        if (stored) {
          try {
            existing = JSON.parse(stored);
          } catch {
            existing = {};
          }
        }
        // 保留首次访问时间（如果之前有），但新 UTM 覆盖
        const finalData: Partial<UtmData> = {
          ...existing,
          ...data,
        };
        localStorage.setItem(UTM_KEY, JSON.stringify(finalData));
        // 同步写到 cookie，服务端也能读到
        setUtmCookie(finalData);
      } catch {
        // ignore
      }
    } else {
      // 没有 UTM，检查是否有 cookie 或 localStorage
      try {
        const stored = localStorage.getItem(UTM_KEY);
        if (stored) {
          // 同步到 cookie
          const data = JSON.parse(stored);
          setUtmCookie(data);
        } else if (!getUtmCookie()) {
          // 首次访问且没有 UTM，记录 direct
          const data: Partial<UtmData> = {
            utm_source: "direct",
            utm_medium: "none",
            landing_page: window.location.pathname,
            referrer: document.referrer || "direct",
            first_visit_at: new Date().toISOString(),
          };
          localStorage.setItem(UTM_KEY, JSON.stringify(data));
          setUtmCookie(data);
        }
      } catch {
        // ignore
      }
    }
  }, []);
}

function setUtmCookie(data: Partial<UtmData>) {
  try {
    const encoded = encodeURIComponent(JSON.stringify(data));
    const expires = new Date(Date.now() + UTM_TTL_MS).toUTCString();
    document.cookie = `${UTM_KEY}=${encoded}; expires=${expires}; path=/; SameSite=Lax`;
  } catch {
    // ignore
  }
}

function getUtmCookie(): string | null {
  try {
    const match = document.cookie.match(new RegExp("(?:^|; )" + UTM_KEY + "=([^;]*)"));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

export function getUtmParams(): Partial<UtmData> {
  if (typeof window === "undefined") return {};
  try {
    const stored = localStorage.getItem(UTM_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore
  }
  return {};
}

export function appendUtmToFormData(formData: Record<string, unknown>): Record<string, unknown> {
  const utm = getUtmParams();
  return { ...formData, utm_source: utm.utm_source || null, utm_medium: utm.utm_medium || null, utm_campaign: utm.utm_campaign || null };
}
